"use client";

import { superscriptInt } from "@/lib/tool-kit/biology-lab/format";
import {
  type Fit,
  type Point,
  predict,
} from "@/lib/tool-kit/biology-lab/standard-curve";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

const WIDTH = 640;
const HEIGHT = 380;
const LEFT = 60;
const RIGHT = 20;
const TOP = 16;
const BOTTOM = 46;
const CURVE_STEPS = 160;

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export interface PlotSample {
  concentration: number;
  reading: number;
}

function xDomain(xs: readonly number[], logX: boolean): [number, number] {
  const positive = xs.filter((x) => x > 0);
  const max = Math.max(...xs);
  if (!logX) {
    return [0, max];
  }
  return [Math.min(...positive), max];
}

function curvePath(
  fit: Fit,
  lo: number,
  hi: number,
  logX: boolean,
  px: (x: number) => number,
  py: (y: number) => number
): string {
  const parts: string[] = [];
  for (let i = 0; i <= CURVE_STEPS; i += 1) {
    const f = i / CURVE_STEPS;
    const x = logX ? lo * (hi / lo) ** f : lo + (hi - lo) * f;
    const y = predict(fit, x);
    if (Number.isFinite(y)) {
      parts.push(
        `${parts.length === 0 ? "M" : "L"}${px(x).toFixed(1)} ${py(y).toFixed(1)}`
      );
    }
  }
  return parts.join(" ");
}

/** Standards, the fitted curve and the unknowns read from it. */
export function StandardCurvePlot({
  points,
  fit,
  samples,
  logX,
  fmt,
  label,
  xLabel,
  yLabel,
}: {
  points: readonly Point[];
  fit: Fit;
  samples: readonly PlotSample[];
  logX: boolean;
  fmt: Format;
  label: string;
  xLabel: string;
  yLabel: string;
}) {
  const shown = logX ? points.filter((p) => p.x > 0) : points;
  const xs = [
    ...shown.map((p) => p.x),
    ...samples.map((s) => s.concentration).filter((c) => c > 0),
  ];
  const [lo, hi] = xDomain(xs, logX);
  const ys = [...shown.map((p) => p.y), ...samples.map((s) => s.reading)];
  const yAxis = niceTicks(Math.min(...ys, 0), Math.max(...ys), 6);
  const linearX = niceTicks(0, hi, 6);
  const logLow = Math.floor(Math.log10(lo));
  const logHigh = Math.max(Math.ceil(Math.log10(hi)), logLow + 1);
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const px = (x: number) => {
    if (logX) {
      return (
        LEFT +
        ((Math.log10(Math.max(x, 1e-300)) - logLow) / (logHigh - logLow)) *
          plotWidth
      );
    }
    return (
      LEFT + ((x - linearX.lo) / (linearX.hi - linearX.lo || 1)) * plotWidth
    );
  };
  const py = (y: number) =>
    TOP +
    plotHeight -
    ((y - yAxis.lo) / (yAxis.hi - yAxis.lo || 1)) * plotHeight;
  const xTicks = logX
    ? Array.from({ length: logHigh - logLow + 1 }, (_, i) => ({
        at: 10 ** (logLow + i),
        text: `10${superscriptInt(logLow + i)}`,
      }))
    : linearX.ticks.map((tick) => ({
        at: tick,
        text: fmt(tick, { maximumSignificantDigits: 3 }),
      }));
  return (
    <svg
      aria-label={label}
      className="h-auto w-full text-muted-foreground"
      role="img"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
    >
      {yAxis.ticks.map((tick) => (
        <g key={`y${tick}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={LEFT}
            x2={WIDTH - RIGHT}
            y1={py(tick)}
            y2={py(tick)}
          />
          <text
            dominantBaseline="middle"
            fill="currentColor"
            fontSize={12}
            textAnchor="end"
            x={LEFT - 8}
            y={py(tick)}
          >
            {fmt(tick, { maximumSignificantDigits: 3 })}
          </text>
        </g>
      ))}
      {xTicks.map((tick) => (
        <g key={`x${tick.at}`}>
          <line
            stroke="currentColor"
            strokeOpacity={0.2}
            x1={px(tick.at)}
            x2={px(tick.at)}
            y1={TOP}
            y2={TOP + plotHeight}
          />
          <text
            fill="currentColor"
            fontSize={12}
            textAnchor="middle"
            x={px(tick.at)}
            y={TOP + plotHeight + 18}
          >
            {tick.text}
          </text>
        </g>
      ))}
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        x={LEFT + plotWidth / 2}
        y={HEIGHT - 6}
      >
        {xLabel}
      </text>
      <text
        fill="currentColor"
        fontSize={12}
        textAnchor="middle"
        transform={`rotate(-90 14 ${TOP + plotHeight / 2})`}
        x={14}
        y={TOP + plotHeight / 2}
      >
        {yLabel}
      </text>
      <path
        d={curvePath(fit, lo, hi, logX, px, py)}
        fill="none"
        stroke={CHART_COLORS[1]}
        strokeWidth={2.5}
      />
      {shown.map((p, index) => (
        <circle
          cx={px(p.x)}
          cy={py(p.y)}
          fill={CHART_COLORS[0]}
          fillOpacity={0.85}
          // Replicates can coincide, so position is the only identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: data points by position
          key={index}
          r={4.5}
        />
      ))}
      {samples
        .filter((s) => s.concentration > 0 || !logX)
        .map((s, index) => (
          <rect
            fill={CHART_COLORS[2]}
            height={9}
            // biome-ignore lint/suspicious/noArrayIndexKey: samples by position
            key={index}
            transform={`rotate(45 ${px(s.concentration)} ${py(s.reading)})`}
            width={9}
            x={px(s.concentration) - 4.5}
            y={py(s.reading) - 4.5}
          />
        ))}
    </svg>
  );
}
